import { ref } from "vue";

export interface Toast {
  id: number;
  message: string;
  type: "error" | "success";
}

/** Singleton toast queue — shared across all components. */
const toasts = ref<Toast[]>([]);
let nextId = 0;

/**
 * Global toast notification system.
 *
 * Uses module-level singleton state so any component can trigger a toast
 * without props, events, or provide/inject. Mount `<ToastProvider />` once
 * in `App.vue` to render the toast queue.
 *
 * @example
 * ```ts
 * const { showToast } = useToast();
 * showToast("success", "User created!");
 * showToast("error", "Something went wrong");
 * ```
 */
export function useToast() {
  function showToast(type: "error" | "success", message: string, duration = 3000) {
    const id = nextId++;
    toasts.value.push({ id, message, type });
    setTimeout(() => dismissToast(id), duration);
  }

  function dismissToast(id: number) {
    const index = toasts.value.findIndex((t) => t.id === id);
    if (index !== -1) {
      toasts.value.splice(index, 1);
    }
  }

  return {
    dismissToast,
    showToast,
    toasts,
  };
}
